import { FC, useMemo } from "react";
import { CheckCircle, Clock, FileText } from "lucide-react";
import { JobStatus } from "~/utils/constants";
import { TServiceTicket } from "~/types";

type Props = {
  serviceTickets: TServiceTicket[];
};

export const ServiceStats: FC<Props> = ({ serviceTickets }) => {
  const stats = useMemo(() => {
    const completedTickets = serviceTickets.filter(
      (t) => t.status === JobStatus.COMPLETED
    ).length;
    const inProgressTickets = serviceTickets.filter(
      (t) => t.status === JobStatus.IN_PROGRESS
    ).length;
    const scheduledTickets = serviceTickets.filter(
      (t) => t.status === JobStatus.SCHEDULED
    ).length;
    const estimatedTickets = serviceTickets.filter(
      (t) => t.status === JobStatus.ESTIMATED
    ).length;

    return {
      total: serviceTickets.length,
      completedTickets,
      inProgressTickets,
      scheduledTickets,
      estimatedTickets,
    };
  }, [serviceTickets]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
      {/* Total Services */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Total Services</span>
          <CheckCircle className="text-blue-600" size={20} />
        </div>
        <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
      </div>

      {/* Completed */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Completed</span>
          <CheckCircle className="text-green-600" size={20} />
        </div>
        <p className="text-3xl font-bold text-green-600">
          {stats.completedTickets}
        </p>
      </div>

      {/* In Progress */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">In Progress</span>
          <Clock className="text-blue-600" size={20} />
        </div>
        <p className="text-3xl font-bold text-blue-600">
          {stats.inProgressTickets}
        </p>
      </div>

      {/* Scheduled */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Scheduled</span>
          <Clock className="text-gray-600" size={20} />
        </div>
        <p className="text-3xl font-bold text-gray-600">
          {stats.scheduledTickets}
        </p>
      </div>

      {/* Estimates */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-600">Estimates</span>
          <FileText className="text-amber-600" size={20} />
        </div>
        <p className="text-3xl font-bold text-amber-600">
          {stats.estimatedTickets}
        </p>
      </div>
    </div>
  );
};
